import React from 'react';
import DomainMappingToolbar from '../components/DomainMappingToolbar';
import DomainMappingTable from '../components/DomainMappingTable';
import AddMappingModal from '../modals/AddMappingModal';
import { Check, PenLine, Info } from 'lucide-react';
import { DeleteWarningModal } from '../../../components/DeleteWarningModal';
import {
    computeMappingMetrics,
    toIdList,
    isClickOutside,
    nextFilterSet,
    getScopedFunctions,
    filterDomainMappings,
    withMappingsEnabled,
    resolveMappingToggleEnabled,
    withoutMappingsById,
} from '../utils/helpers';
import {
    saveConfigureState,
    mergeConfigureState,
    getEntitySlice,
    hydrateConfigureMappingsFromApi,
    getDefaultEnvironmentId
} from '../utils/persistence';
import { buildConfigureIndexes } from '../utils/indexes';
import { FILTER_ALL } from '../utils/constants';
import Load from '../../../components/loadAction';
import {
    createConfigureBusinessMapping,
    updateConfigureBusinessMapping,
    buildCreateMappingApiPayload,
    buildUpdateMappingApiPayload,
    isConfigureMappingMutationSuccess
} from '../../../utils/configureMappingEventHandle';

class DomainMappingView extends React.Component {
    constructor(props) {
        super(props);
        this.mappingTableRef = React.createRef();
        this.infoPopupRef = React.createRef();
        this.infoIconRef = React.createRef();
        this.state = {
            businesses: [],
            functions: [],
            kpis: [],
            models: [],
            assets: [],
            mappings: [],
            debouncedSearchVal: '',
            modalOpen: false,
            editingMapping: null,
            filterBusinesses: new Set(),
            filterFunctions: new Set(),
            filterKpis: new Set(),
            filterStatus: FILTER_ALL,
            showDeleteModal: false,
            deleteMappingIds: [],
            showDetailedMetrics: false,
            isFullyExpanded: true,
            showMappedKpis: true,
            catalogueLoading: true,
            pageMetrics: null,
            mappingSubmitting: false,
            filterEnvironment: getDefaultEnvironmentId(props.environment)
        };
    }

    getIndexes = (state = this.state) => {
        const ref = this._indexRef;
        if (
            this._indexCache
            && ref
            && ref.businesses === state.businesses
            && ref.functions === state.functions
            && ref.kpis === state.kpis
            && ref.models === state.models
            && ref.assets === state.assets
            && ref.mappings === state.mappings
        ) {
            return this._indexCache;
        }

        this._indexRef = {
            businesses: state.businesses,
            functions: state.functions,
            kpis: state.kpis,
            models: state.models,
            assets: state.assets,
            mappings: state.mappings
        };
        this._indexCache = buildConfigureIndexes(state);
        return this._indexCache;
    };

    persistState = (partial, extraLocalState = {}) => {
        this.setState((prevState) => {
            const next = mergeConfigureState(prevState, partial);
            saveConfigureState(next);
            return {
                ...getEntitySlice(next),
                ...extraLocalState
            };
        });
    };

    mergeEntitySliceFromEvent = (incomingState = {}, prevState = this.state) => {
        const incoming = getEntitySlice(incomingState);
        const merged = { ...incoming };

        // localStorage / cross-tab updates may only carry assets+mappings; keep hierarchy for joins
        if (!incoming.kpis.length && prevState.kpis.length) merged.kpis = prevState.kpis;
        if (!incoming.functions.length && prevState.functions.length) {
            merged.functions = prevState.functions;
        }
        if (!incoming.businesses.length && prevState.businesses.length) {
            merged.businesses = prevState.businesses;
        }
        if (!incoming.models.length && prevState.models.length) merged.models = prevState.models;

        return merged;
    };

    handleStateUpdate = (e) => {
        this.setState((prev) => this.mergeEntitySliceFromEvent(e.detail, prev));
    };

    presetNeedsHierarchyHydrate = (preset) => {
        if (!preset) return true;
        const slice = getEntitySlice(preset);
        const hasMappings = slice.mappings.length > 0;
        const hasHierarchy = slice.kpis.length > 0 || slice.functions.length > 0;
        return hasMappings && !hasHierarchy;
    };

    loadMappingsFromApi = (envId = this.state.filterEnvironment) => {
        this.setState({ catalogueLoading: true });
        return hydrateConfigureMappingsFromApi(envId).then(({ state, metrics }) => {
            this.setState({
                ...getEntitySlice(state),
                pageMetrics: metrics || null,
                catalogueLoading: false
            });
        });
    };

    componentDidMount() {
        const preset = this.props.initialCatalogueState;
        if (this.props.skipInitialFetch && preset && !this.presetNeedsHierarchyHydrate(preset)) {
            this.setState({
                ...getEntitySlice(preset),
                pageMetrics: this.props.pageMetrics || null,
                catalogueLoading: false
            });
        } else if (this.state.filterEnvironment !== null) {
            this.loadMappingsFromApi();
        }
        window.addEventListener('qualdo-state-update', this.handleStateUpdate);
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentDidUpdate(prevProps) {
        if (!this.state.filterEnvironment && this.props.environment !== prevProps.environment) {
            const envId = getDefaultEnvironmentId(this.props.environment);
            if (envId !== null) {
                this.setState({ filterEnvironment: envId });
                this.loadMappingsFromApi(envId);
                return;
            }
        }
        if (
            this.props.skipInitialFetch
            && this.props.initialCatalogueState
            && this.props.initialCatalogueState !== prevProps.initialCatalogueState
        ) {
            if (this.presetNeedsHierarchyHydrate(this.props.initialCatalogueState)) {
                this.loadMappingsFromApi();
            } else {
                this.setState({
                    ...getEntitySlice(this.props.initialCatalogueState),
                    pageMetrics: this.props.pageMetrics || null,
                    catalogueLoading: false
                });
            }
        } else if (this.props.pageMetrics !== prevProps.pageMetrics && this.props.pageMetrics) {
            this.setState({ pageMetrics: this.props.pageMetrics });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('qualdo-state-update', this.handleStateUpdate);
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleSearchChange = (val) => {
        this.setState({ debouncedSearchVal: val });
    };

    handleClickOutside = (event) => {
        if (
            this.state.showDetailedMetrics &&
            isClickOutside(event, this.infoPopupRef.current, this.infoIconRef.current)
        ) {
            this.setState({ showDetailedMetrics: false });
        }
    };

    callMappingTable = (methodName, nextExpanded) => {
        const table = this.mappingTableRef.current;
        if (table && typeof table[methodName] === 'function') {
            table[methodName]();
        }
        this.setState({ isFullyExpanded: nextExpanded });
    };

    handleExpandAll = () => this.callMappingTable('handleExpandAll', true);

    handleCollapseAll = () => this.callMappingTable('handleCollapseAll', false);

    getMetrics = (indexes = this.getIndexes()) => {
        if (this.state.pageMetrics) {
            return this.state.pageMetrics;
        }
        return computeMappingMetrics(this.state.mappings, this.state.kpis, indexes.isKpiEff);
    };

    handleBusinessFilterChange = (bizIdOrIds, checked) => {
        this.setState({
            filterBusinesses: nextFilterSet(this.state.filterBusinesses, bizIdOrIds, checked),
            filterFunctions: new Set(),
            filterKpis: new Set()
        });
    };

    handleFunctionFilterChange = (fnIdOrIds) => {
        this.setState({
            filterFunctions: nextFilterSet(this.state.filterFunctions, fnIdOrIds),
            filterKpis: new Set()
        });
    };

    handleKpiFilterChange = (kpiIdOrIds) => {
        this.setState({
            filterKpis: nextFilterSet(this.state.filterKpis, kpiIdOrIds)
        });
    };

    clearAllFilters = () => {
        this.setState({
            filterBusinesses: new Set(),
            filterFunctions: new Set(),
            filterKpis: new Set(),
            filterStatus: FILTER_ALL,
            debouncedSearchVal: ''
        });
    };

    setMappingsEnabled = (mappingIds, enabled) => {
        const nextMappings = withMappingsEnabled(this.state.mappings, mappingIds, enabled);
        if (nextMappings === this.state.mappings) return;
        this.persistState({ mappings: nextMappings });
    };

    handleToggleEnable = (mappingIdOrIds) => {
        const resolved = resolveMappingToggleEnabled(
            this.state.mappings,
            mappingIdOrIds,
            this.getIndexes()
        );
        if (!resolved) return;
        this.setMappingsEnabled(resolved.ids, resolved.enabled);
    };

    openDeleteModal = (mappingIdOrIds) => {
        const ids = toIdList(mappingIdOrIds);
        if (!ids.length) return;
        this.setState({ showDeleteModal: true, deleteMappingIds: ids });
    };

    handleDeleteClose = () => {
        this.setState({ showDeleteModal: false, deleteMappingIds: [] });
    };

    handleConfirmDelete = () => {
        const { deleteMappingIds } = this.state;
        if (!deleteMappingIds.length) return;
        this.persistState(
            { mappings: withoutMappingsById(this.state.mappings, deleteMappingIds) },
            { showDeleteModal: false, deleteMappingIds: [] }
        );
    };

    handleEditMapping = (mapping) => {
        this.setState({ editingMapping: mapping, modalOpen: true });
    };

    handleSaveMapping = async (payload) => {
        const isEdit = !!this.state.editingMapping;
        const context = { kpis: this.state.kpis, assets: this.state.assets };
        const apiPayload = isEdit
            ? buildUpdateMappingApiPayload(payload, context)
            : buildCreateMappingApiPayload(payload, context);
        const showToast = this.props.showToast;

        this.setState({ mappingSubmitting: true });

        try {
            const response = isEdit
                ? await updateConfigureBusinessMapping(apiPayload)
                : await createConfigureBusinessMapping(apiPayload);

            if (!isConfigureMappingMutationSuccess(response)) {
                if (typeof showToast === 'function') {
                    showToast(
                        'alert-danger',
                        response?.message || 'Failed to save mapping',
                        null,
                        null,
                        false
                    );
                }
                this.setState({ mappingSubmitting: false });
                return;
            }

            const savedEnv = payload.envId || this.state.filterEnvironment;
            const { state, metrics } = await hydrateConfigureMappingsFromApi(savedEnv);
            this._indexRef = null;
            this._indexCache = null;
            this.setState({
                ...getEntitySlice(state),
                pageMetrics: metrics || this.state.pageMetrics,
                filterEnvironment: savedEnv || this.state.filterEnvironment,
                modalOpen: false,
                editingMapping: null,
                mappingSubmitting: false
            });
            if (typeof showToast === 'function') {
                showToast(
                    'alert-success',
                    isEdit ? 'Mapping updated' : 'Mapping added',
                    null,
                    null,
                    false
                );
            }
        } catch (e) {
            console.error('Mapping save failed:', e);
            if (typeof showToast === 'function') {
                showToast('alert-danger', 'Failed to save mapping', null, null, false);
            }
            this.setState({ mappingSubmitting: false });
        }
    };

    handleEnvironmentChange = (envId) => {
        if (String(envId) === String(this.state.filterEnvironment)) return;
        this.setState({ filterEnvironment: envId }, () => this.loadMappingsFromApi(envId));
    };

    getFilteredMappings = (indexes = this.getIndexes()) => {
        const {
            mappings,
            debouncedSearchVal,
            filterBusinesses,
            filterFunctions,
            filterKpis,
            filterStatus
        } = this.state;
        return filterDomainMappings(
            mappings,
            { searchVal: debouncedSearchVal, filterBusinesses, filterFunctions, filterKpis, filterStatus },
            indexes
        );
    };

    render() {
        if (this.state.catalogueLoading) {
            return <Load />;
        }

        const {
            businesses,
            functions,
            kpis,
            models,
            assets,
            modalOpen,
            editingMapping,
            debouncedSearchVal,
            filterBusinesses,
            filterFunctions,
            filterKpis,
            filterStatus,
            showMappedKpis
        } = this.state;

        const indexes = this.getIndexes();
        const metrics = this.getMetrics(indexes);
        const { mappedPercent, autoCount, manualCount } = metrics;
        const activeBusinesses = businesses.filter(indexes.isBizEff);
        const scopedFunctions = getScopedFunctions(functions, filterBusinesses, indexes.isFnEff);

        const activeMappings = this.getFilteredMappings(indexes);
        const deleteCount = this.state.deleteMappingIds.length;

        return (
            <div className="portlet pb-0">
                <div className="portlet-title tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-justify-between tw-gap-3 after:!tw-content-none after:!tw-hidden before:!tw-content-none before:!tw-hidden">
                    <div className="caption tw-min-w-0">
                        <span className="text-uppercase font-dark tw-text-lg tw-font-bold tw-tracking-wide tw-whitespace-nowrap">Data Assets Mapping</span>
                        <small className='tw-text-sm tw-text-[#535862] tw-block tw-mt-1'>Map each KPI to the datasource, table & attribute that powers it — overrides are <strong>Manual</strong>.</small>
                    </div>
                    <div className="actions !tw-flex tw-items-center tw-gap-2 tw-relative tw-shrink-0">
                        <div className="tw-flex tw-items-center tw-justify-center tw-px-3 tw-py-1 tw-bg-[#EFF2FE] dark:tw-bg-white/5 tw-border tw-border-solid tw-border-[#C7D2FE] dark:tw-border-white/10 tw-text-xs tw-font-semibold tw-text-[#6366F1] dark:tw-text-[#818CF8] tw-rounded-full">
                            {mappedPercent}% mapped
                        </div>
                        <span className="tw-text-[#D5D7DA] dark:tw-text-white/20 tw-text-xs">·</span>
                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-[#047857]">
                            <Check size={12} className="tw-inline-block tw-mr-1 tw-text-[#047857]" />
                            <span>{autoCount} auto</span>
                        </span>
                        <span className="tw-text-[#D5D7DA] dark:tw-text-white/20 tw-text-xs">·</span>
                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-[#4338CA]">
                            <PenLine size={12} className="tw-inline-block tw-mr-1 tw-text-[#4338CA]" />
                            <span>{manualCount} manual</span>
                        </span>
                        <button
                            type="button"
                            ref={this.infoIconRef}
                            onClick={() => this.setState(prev => ({ showDetailedMetrics: !prev.showDetailedMetrics }))}
                            className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-border-0 tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-white tw-bg-transparent hover:tw-bg-slate-100 dark:hover:tw-bg-white/10 tw-cursor-pointer tw-p-0 tw-transition-colors"
                        >
                            <Info size={12} />
                        </button>

                        {this.state.showDetailedMetrics && (
                            <div
                                ref={this.infoPopupRef}
                                className="tw-absolute tw-right-0 tw-top-[calc(100%+8px)] tw-z-[100] tw-w-[280px] tw-bg-white dark:tw-bg-[#1E1E2D] tw-border tw-border-solid tw-border-[#E4E7EC] dark:tw-border-white/10 tw-rounded-xl tw-shadow-[0px_12px_40px_rgba(0,0,0,0.12)] tw-p-4 tw-text-left"
                            >
                                <h4 className="tw-text-sm tw-font-bold tw-text-gray-900 dark:tw-text-white tw-mb-3 tw-mt-0">
                                    Mapping calculations
                                </h4>
                                <div className="tw-border-0 tw-border-b tw-border-solid tw-border-[#F3F4F6] dark:tw-border-white/10 tw-my-2"></div>

                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-[10px] tw-font-bold tw-text-[#717680] dark:tw-text-[#B2B5BE] tw-uppercase tw-tracking-wider">
                                        Coverage · % mapped
                                    </span>
                                    <span className="tw-text-sm tw-font-bold tw-text-[#6366F1] dark:tw-text-[#818CF8]">
                                        {mappedPercent}%
                                    </span>
                                </div>

                                <div className="tw-bg-[#EFF2FE] dark:tw-bg-white/5 tw-text-[#6366F1] dark:tw-text-[#818CF8] tw-text-[11px] tw-font-semibold tw-rounded-lg tw-px-2 tw-py-1.5 tw-text-center tw-my-3">
                                    {metrics.mappedCount} ÷ {metrics.totalActiveKpis} active KPIs × 100
                                </div>

                                <div className="tw-space-y-1.5">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                        <span className="tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">Active KPIs</span>
                                        <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.totalActiveKpis}</span>
                                    </div>
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                        <span className="tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">With an enabled mapping</span>
                                        <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.mappedCount}</span>
                                    </div>
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                        <span className="tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">Still unmapped</span>
                                        <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.unmappedCount}</span>
                                    </div>
                                </div>

                                <div className="tw-border-0 tw-border-t tw-border-solid tw-border-[#F3F4F6] dark:tw-border-white/10 tw-my-3"></div>

                                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                    <span className="tw-text-[10px] tw-font-bold tw-text-[#717680] dark:tw-text-[#B2B5BE] tw-uppercase tw-tracking-wider">
                                        Mappings by source
                                    </span>
                                    <span className="tw-text-xs tw-font-bold tw-text-gray-900 dark:tw-text-white">
                                        {metrics.totalMappings} total
                                    </span>
                                </div>

                                <div className="tw-space-y-1.5">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                        <span className="tw-flex tw-items-center tw-gap-2 tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">
                                            <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-[#10B981]"></span>
                                            Auto-mapped by Qualdo
                                        </span>
                                        <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.autoCount}</span>
                                    </div>
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                        <span className="tw-flex tw-items-center tw-gap-2 tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">
                                            <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-[#6366F1]"></span>
                                            Manual overrides
                                        </span>
                                        <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.manualCount}</span>
                                    </div>
                                </div>

                                <div className="tw-flex tw-h-1.5 tw-w-full tw-rounded-full tw-overflow-hidden tw-bg-gray-100 dark:tw-bg-white/10 tw-my-3">
                                    <div className="tw-bg-[#10B981]" style={{ width: `${metrics.totalMappings > 0 ? (metrics.autoCount / metrics.totalMappings) * 100 : 0}%` }} />
                                    <div className="tw-bg-[#6366F1]" style={{ width: `${metrics.totalMappings > 0 ? (metrics.manualCount / metrics.totalMappings) * 100 : 0}%` }} />
                                </div>

                                <div className="tw-border-0 tw-border-t tw-border-solid tw-border-[#F3F4F6] dark:tw-border-white/10 tw-my-3"></div>

                                <p className="tw-text-[10px] tw-text-[#717680] dark:tw-text-[#B2B5BE] tw-leading-relaxed tw-m-0">
                                    Inactive KPIs are excluded from coverage. A KPI can have several mappings, so Auto + Manual = total mappings, not KPI count.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className='portlet-body !tw-p-0 tw-relative !tw-overflow-visible'>
                    <DomainMappingToolbar
                        businesses={businesses}
                        functions={functions}
                        kpis={kpis}
                        searchVal={debouncedSearchVal}
                        onSearchChange={this.handleSearchChange}
                        onAddMappingClick={() => this.setState({ modalOpen: true, editingMapping: null })}
                        mappedPercent={mappedPercent}
                        autoCount={autoCount}
                        manualCount={manualCount}
                        activeBusinesses={activeBusinesses}
                        filterBusinesses={filterBusinesses}
                        onBusinessFilterToggle={this.handleBusinessFilterChange}
                        scopedFunctions={scopedFunctions}
                        filterFunctions={filterFunctions}
                        onFunctionFilterChange={this.handleFunctionFilterChange}
                        filterKpis={filterKpis}
                        onKpiFilterChange={this.handleKpiFilterChange}
                        filterStatus={filterStatus}
                        onStatusFilterChange={(val) => this.setState({ filterStatus: val || FILTER_ALL })}
                        clearAllFilters={this.clearAllFilters}
                        matchingCount={activeMappings.length}
                        isFullyExpanded={this.state.isFullyExpanded}
                        onExpandAll={this.handleExpandAll}
                        onCollapseAll={this.handleCollapseAll}
                        showMappedKpis={showMappedKpis}
                        onShowMappedToggle={() => this.setState((prev) => ({ showMappedKpis: !prev.showMappedKpis }))}
                        environments={this.props.environment}
                        filterEnvironment={this.state.filterEnvironment}
                        onEnvironmentChange={this.handleEnvironmentChange}
                    />

                    <div className="tw-w-full tw-flex tw-flex-col">
                        <div className="tw-w-full tw-min-w-0 tw-flex tw-flex-col">
                            <DomainMappingTable
                                ref={this.mappingTableRef}
                                businesses={businesses}
                                functions={functions}
                                kpis={kpis}
                                models={models}
                                assets={assets}
                                mappings={activeMappings}
                                allMappings={this.state.mappings}
                                indexes={indexes}
                                showMappedKpis={showMappedKpis}
                                filterBusinesses={filterBusinesses}
                                filterFunctions={filterFunctions}
                                filterKpis={filterKpis}
                                filterStatus={filterStatus}
                                filterEnvironment={this.state.filterEnvironment}
                                searchVal={this.state.debouncedSearchVal}
                                searchHighlight={this.state.debouncedSearchVal}
                                onToggleEnable={this.handleToggleEnable}
                                onDeleteMapping={this.openDeleteModal}
                                onBulkDelete={this.openDeleteModal}
                                onBulkEnable={(ids) => this.setMappingsEnabled(ids, true)}
                                onBulkDisable={(ids) => this.setMappingsEnabled(ids, false)}
                                onEditMapping={this.handleEditMapping}
                            />
                        </div>
                    </div>

                    <AddMappingModal
                        show={modalOpen}
                        onClose={() => this.setState({ modalOpen: false, editingMapping: null, mappingSubmitting: false })}
                        onSave={this.handleSaveMapping}
                        submitting={this.state.mappingSubmitting}
                        editingMapping={editingMapping}
                        businesses={activeBusinesses}
                        functions={functions.filter(indexes.isFnEff)}
                        kpis={kpis.filter(indexes.isKpiEff)}
                        assets={assets}
                        mappings={this.state.mappings}
                        environments={this.props.environment}
                        filterEnvironment={this.state.filterEnvironment}
                    />

                    <DeleteWarningModal
                        delete_show={this.state.showDeleteModal}
                        deleteClose={this.handleDeleteClose}
                        handleDeleteFunction={this.handleConfirmDelete}
                        component={deleteCount > 1 ? `${deleteCount} KPI Mappings` : 'KPI Mapping'}
                        modalTitle={deleteCount > 1 ? 'KPI Mappings' : 'KPI Mapping'}
                    />
                </div>
            </div>
        );
    }
}

export default DomainMappingView;
